import { useCallback } from 'react';
import { supabase, Database } from './supabase';
import { uploadImageToStorage, getPublicUrl } from './storageHelpers';

const STORAGE_BUCKET = 'playmix-assets';
const IMAGE_FOLDER = 'animation-assets';

export type Animation = Database['public']['Tables']['animation']['Row'];

export type AnimationHistoryEntry = Database['public']['Tables']['animation_history_entries']['Row'];

export type AnimationWithHistory = Animation & {
  animation_history_entries: AnimationHistoryEntry[];
};

export const useAnimate = () => {
  /**
   * Creates a new animation in the database
   * @param animationName - The name of the animation
   * @returns The created Animation object
   */
  const createAnimation = useCallback(async (animationName: string): Promise<Animation> => {
    // console.log('Creating new animation with name:', animationName);
    
    const { data: animation, error: animationError } = await supabase
      .from('animation')
      .insert({
        animation_name: animationName,
      })
      .select('*')
      .single();

    if (animationError) {
      console.error('Error creating animation:', animationError);
      throw animationError;
    }
    if (!animation) {
      console.error('No animation returned after creation');
      throw new Error('No animation returned after creation');
    }
    
    // console.log('Successfully created animation:', animation);
    return animation;
  }, []);

  /**
   * Updates an existing animation in the database
   * @param animationId - The ID of the animation to update
   * @param updates - The fields to update
   * @returns The updated Animation object
   */
  const updateAnimation = useCallback(async (animationId: string, updates: Partial<Animation>): Promise<Animation> => {
    const { data: animation, error: animationError } = await supabase
      .from('animation')
      .update(updates)
      .eq('id', animationId)
      .select('*')
      .single();

    if (animationError) throw animationError;
    if (!animation) throw new Error('No animation returned after update');
    return animation;
  }, []);

  /**
   * Retrieves an animation by its ID, without history entries
   * @param animationId - The ID of the animation to retrieve
   * @returns The Animation object or null if not found
   */
  const getAnimation = useCallback(async (animationId: string): Promise<Animation | null> => {
    const { data: animation, error: animationError } = await supabase
      .from('animation')
      .select('*')
      .eq('id', animationId)
      .single();

    if (animationError) {
      if (animationError.code === 'PGRST116') {
        // Record not found
        return null;
      }
      console.error('Error retrieving animation:', animationError);
      throw animationError;
    }
    
    return animation;
  }, []);

  /**
   * Creates a new history entry for an animation
   * @param animationId - The ID of the animation
   * @param user_prompt - Description of the animation
   * @param referenceImage - Base64 or URL of the reference image
   * @param animationImages - Array of base64 or URLs of animation frames
   * @param enhanced_prompt - Enhanced description of the animation
   * @param animation_video - Base64 or URL of the animation video
   * @param referenceImageDescription - Description of the reference image
   * @returns The created AnimationHistoryEntry object
   */
  const createHistoryEntry = useCallback(async (
    animationId: string,
    user_prompt: string,
    referenceImage: string,
    animationImages?: string[],
    enhanced_prompt?: string,
    animation_video?: string,
    referenceImageDescription?: string,
  ): Promise<AnimationHistoryEntry> => {
    // Handle reference image upload or reuse
    let publicReferenceUrl = referenceImage;
    if (referenceImage && (referenceImage.startsWith('data:') || referenceImage.startsWith('blob:'))) {
      const referenceImagePath = await uploadImageToStorage(
        supabase, 
        referenceImage, 
        `animation-${animationId}-reference`, 
        IMAGE_FOLDER
      );
      publicReferenceUrl = getPublicUrl(supabase, referenceImagePath);
    }
    
    // Handle animation images upload or reuse
    const storedAnimationImages: string[] = [];
    if (animationImages && animationImages.length > 0) {
      for (let i = 0; i < animationImages.length; i++) {
        const image = animationImages[i];
        if (image && (image.startsWith('data:') || image.startsWith('blob:'))) {
          const imagePath = await uploadImageToStorage(
            supabase,
            image,
            `animation-${animationId}-frame-${i}`,
            IMAGE_FOLDER
          );
          const publicImageUrl = getPublicUrl(supabase, imagePath);
          storedAnimationImages.push(publicImageUrl);
        } else {
          storedAnimationImages.push(image);
        }
        
        if (i < animationImages.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1));
        }
      }
    }

    // Handle animation video upload or reuse
    let publicVideoUrl = animation_video;
    if (animation_video && (animation_video.startsWith('data:') || animation_video.startsWith('blob:'))) {
      const videoPath = await uploadImageToStorage(
        supabase,
        animation_video,
        `animation-${animationId}-video`,
        IMAGE_FOLDER
      );
      publicVideoUrl = getPublicUrl(supabase, videoPath);
    }
    
    const { data: entry, error: entryError } = await supabase
      .from('animation_history_entries')
      .insert({
        animation_id: animationId,
        user_prompt,
        reference_image: publicReferenceUrl,
        animation_images: storedAnimationImages.length > 0 ? storedAnimationImages : [],
        enhanced_prompt: enhanced_prompt,
        animation_video: publicVideoUrl,
        reference_image_description: referenceImageDescription,
      })
      .select('*')
      .single();

    if (entryError) {
      console.error('Error creating animation history entry:', entryError);
      throw entryError;
    }
    if (!entry) {
      console.error('No entry returned after creation');
      throw new Error('No entry returned after creation');
    }
    
    return entry;
  }, []);

  /**
   * Updates an existing animation history entry
   * @param entryId - The ID of the history entry to update
   * @param updates - The fields to update
   * @returns The updated AnimationHistoryEntry object
   */
  const updateHistoryEntry = useCallback(async (
    entryId: string,
    updates: Partial<AnimationHistoryEntry>
  ): Promise<AnimationHistoryEntry> => {
    // Get the current entry to determine the animation ID
    const { data: currentEntry, error: getError } = await supabase
      .from('animation_history_entries')
      .select('*')
      .eq('id', entryId)
      .single();
      
    if (getError) {
      console.error('Error retrieving current animation history entry:', getError);
      throw getError;
    }
    
    const animationId = currentEntry.animation_id;
    const updateData: Partial<AnimationHistoryEntry> = { ...updates };
    
    // Handle reference image upload if provided
    if (updates.reference_image && (updates.reference_image.startsWith('data:') || updates.reference_image.startsWith('blob:'))) {
      const referenceImagePath = await uploadImageToStorage(
        supabase,
        updates.reference_image,
        `animation-${animationId}-reference-update`,
        IMAGE_FOLDER
      );
      // Get the full public URL
      const publicReferenceUrl = getPublicUrl(supabase, referenceImagePath);
      updateData.reference_image = publicReferenceUrl; // Save the full public URL
    }
    
    // Handle animation images upload if provided
    if (updates.animation_images && updates.animation_images.length > 0) {
      const storedAnimationImages: string[] = [];
      for (let i = 0; i < updates.animation_images.length; i++) {
        const image = updates.animation_images[i];
        // Only upload if it's a base64 image
        if (image && (image.startsWith('data:') || image.startsWith('blob:'))) {
          const imagePath = await uploadImageToStorage(
            supabase,
            image,
            `animation-${animationId}-frame-${i}-update`,
            IMAGE_FOLDER
          );
          // Get the full public URL
          const publicImageUrl = getPublicUrl(supabase, imagePath);
          storedAnimationImages.push(publicImageUrl); // Save the full public URL
          
          if (i < updates.animation_images.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1));
          }
        } else {
          // Keep existing storage path (which should already be a public URL after the fix)
          storedAnimationImages.push(image);
        }
      }
      updateData.animation_images = storedAnimationImages;
    }

    // Handle animation video upload if provided
    if (updates.animation_video && (updates.animation_video.startsWith('data:') || updates.animation_video.startsWith('blob:'))) {
      const videoPath = await uploadImageToStorage(
        supabase,
        updates.animation_video,
        `animation-${animationId}-video-update`,
        IMAGE_FOLDER
      );
      const publicVideoUrl = getPublicUrl(supabase, videoPath);
      updateData.animation_video = publicVideoUrl;
    }
    
    const { data: entry, error: entryError } = await supabase
      .from('animation_history_entries')
      .update(updateData)
      .eq('id', entryId)
      .select('*')
      .single();

    if (entryError) {
      console.error('Error updating animation history entry:', entryError);
      throw entryError;
    }
    if (!entry) {
      console.error('No entry returned after update');
      throw new Error('No entry returned after update');
    }
    
    return entry;
  }, []);

  /**
   * Retrieves animation history entries for an animation
   * @param animationId - The ID of the animation to retrieve history for
   * @returns The AnimationHistoryEntry[]
   */
  const getAnimationHistory = useCallback(async (animationId: string): Promise<AnimationHistoryEntry[]> => {
    if (!animationId) {
      return [];
    }
    
    const { data: historyEntries, error: historyError } = await supabase
      .from('animation_history_entries')
      .select('*, created_at')
      .eq('animation_id', animationId);
      
    if (historyError) {
      console.error('Error retrieving animation history:', historyError);
      throw historyError;
    }
    
    const entries = historyEntries || [];
    
    // Sort history entries by updated_at
    const sortedHistoryEntries = [...entries].sort((a, b) => 
      new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime()
    );
    
    // Generate public URLs for all images in the history entries
    const entriesNornalized = sortedHistoryEntries.map(entry => {
      // Create a new object to avoid mutating the original
      const entryWithPublicUrls = { ...entry };
      
      // Generate public URL for reference image if it exists and is a storage path
      // With the fix above, this block is now redundant for new entries, but kept 
      // for compatibility with old entries that only saved the storage path.
      if (entry.reference_image && !entry.reference_image.startsWith('data:') && !entry.reference_image.startsWith('http')) {
        entryWithPublicUrls.reference_image = getPublicUrl(supabase, entry.reference_image);
      }
      
      // Generate public URLs for animation images if they exist and are storage paths
      // With the fix above, this block is now redundant for new entries, but kept 
      // for compatibility with old entries that only saved the storage path.
      if (entry.animation_images && entry.animation_images.length > 0) {
        entryWithPublicUrls.animation_images = entry.animation_images.map(imagePath => {
          if (imagePath && !imagePath.startsWith('data:') && !imagePath.startsWith('http')) {
            return getPublicUrl(supabase, imagePath);
          }
          return imagePath;
        });
      }
      
      return entryWithPublicUrls;
    });
    
    return entriesNornalized as AnimationHistoryEntry[];
  }, []);

  /**
   * Remixes an animation by creating a new animation record and copying a specific or the latest history entry.
   * @param animationId - The ID of the animation to remix
   * @param historyId - Optional ID of a specific history entry to remix
   * @returns The new Animation object with its remixed history entry
   */
  const remixAnimation = useCallback(async (animationId: string, historyId?: string): Promise<AnimationWithHistory> => {
    const originalAnimation = await getAnimation(animationId);
    if (!originalAnimation) {
      throw new Error('Animation not found');
    }

    const history = await getAnimationHistory(animationId);
    let entryToRemix: AnimationHistoryEntry | undefined;

    if (historyId) {
      entryToRemix = history.find(entry => entry.id === historyId);
      if (!entryToRemix) {
        throw new Error(`History entry with ID ${historyId} not found`);
      }
      // console.log('[useAnimate.remixAnimation] Using provided historyId', { animationId, historyId });
    } else {
      if (history.length === 0) {
        throw new Error('No history entries found to remix');
      }
      // Since getAnimationHistory sorts by updated_at ascending, the last one is the most recent
      entryToRemix = history[history.length - 1];
      // console.log('[useAnimate.remixAnimation] Using latest history entry', { animationId, historyId: entryToRemix.id });
    }

    const newAnimation = await createAnimation(originalAnimation.animation_name + ' (copy)');

    // Update relationship and metadata if needed
    // In this schema, we don't have original_animation_id, but we follow the remixGame logic pattern
    await updateAnimation(newAnimation.id, {
      user_id: originalAnimation.user_id,
      hidden: true
    });

    const { data: newHistoryEntry, error: entryError } = await supabase
      .from('animation_history_entries')
      .insert({
        animation_id: newAnimation.id,
        user_prompt: entryToRemix.user_prompt,
        reference_image: entryToRemix.reference_image,
        animation_images: entryToRemix.animation_images,
        enhanced_prompt: entryToRemix.enhanced_prompt,
        animation_video: entryToRemix.animation_video,
        reference_image_description: entryToRemix.reference_image_description,
      })
      .select('*')
      .single();

    if (entryError || !newHistoryEntry) {
      console.error('Error creating remixed history entry:', entryError);
      throw entryError || new Error('No entry returned after remix');
    }

    return {
      ...newAnimation,
      animation_history_entries: [newHistoryEntry]
    };
  }, [getAnimation, getAnimationHistory, createAnimation, updateAnimation]);

  return {
    createAnimation,
    updateAnimation,
    getAnimation,
    createHistoryEntry,
    updateHistoryEntry,
    getAnimationHistory,
    remixAnimation,
  };
};